import React, { useCallback, useEffect, useRef, useState } from 'react';
import { attendanceUiText } from '@/lib/nfcUiVisibility';
import { QRCodeSVG } from 'qrcode.react';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

async function kioskRequest(path, body) {
  const response = await fetch(`${API_BASE}/api/attendance/qr/kiosk/${path}`, {
    method: 'POST',
    cache: 'no-store',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const contentType = response.headers.get('content-type') || '';
  const result = contentType.includes('application/json') ? await response.json() : null;
  if (!response.ok) {
    const error = new Error(result?.detail || `Richiesta non riuscita (${response.status}).`);
    error.status = response.status;
    throw error;
  }
  return result;
}

export default function AttendanceQrKiosk() {
  const [pairingCode, setPairingCode] = useState('');
  const [paired, setPaired] = useState(false);
  const [polling, setPolling] = useState(false);
  const [qr, setQr] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const generationRef = useRef(null);
  const requestInFlight = useRef(false);

  const refreshQr = useCallback(async () => {
    if (requestInFlight.current) return;
    requestInFlight.current = true;
    setLoading(true);
    try {
      const result = await kioskRequest('current', {
        known_generation: generationRef.current,
      });
      if (!result || (!result.qr_payload && generationRef.current === null)) {
        throw new Error('QR non disponibile. Riprova senza associare nuovamente il dispositivo.');
      }
      if (result.refresh_required && result.qr_payload) {
        generationRef.current = result.generation;
        setQr(result);
      } else if (result.expires_at) {
        setQr((previous) => previous ? { ...previous, expires_at: result.expires_at } : previous);
      }
      setPaired(true);
      setPolling(true);
      setError('');
    } catch (requestError) {
      if (requestError.status === 401) {
        setPaired(false);
        setPolling(false);
        setQr(null);
        generationRef.current = null;
        setError('');
      } else {
        setError(requestError.message);
      }
    } finally {
      requestInFlight.current = false;
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let active = true;
    if (active) refreshQr();
    return () => { active = false; };
  }, [refreshQr]);

  useEffect(() => {
    if (!polling) return undefined;
    const timer = window.setInterval(() => refreshQr(), 2000);
    return () => {
      window.clearInterval(timer);
    };
  }, [polling, refreshQr]);

  const enroll = async (event) => {
    event.preventDefault();
    if (busy || !pairingCode.trim()) return;
    setBusy(true);
    setError('');
    try {
      await kioskRequest('enroll', { pairing_code: pairingCode.trim() });
      setPairingCode('');
      generationRef.current = null;
      setPaired(true);
      setPolling(true);
      await refreshQr();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  };

  const expiryText = qr?.expires_at
    ? new Date(qr.expires_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    : '';

  return (
    <main className="min-h-screen bg-slate-950 px-4 py-8 text-white" data-testid="attendance-qr-kiosk">
      <section className="mx-auto flex min-h-[80vh] max-w-xl flex-col items-center justify-center rounded-3xl border border-white/10 bg-white p-6 text-center text-slate-900 shadow-2xl sm:p-10">
        <p className="text-xs font-bold uppercase tracking-[0.22em] text-emerald-700">RestaurantOS · Presenze</p>
        <h1 className="mt-3 font-display text-3xl font-bold">Timbratura QR</h1>
        {paired && qr?.qr_payload ? (
          <>
            <p className="mt-3 text-sm text-slate-600">{attendanceUiText(qr.point_name)}</p>
            <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-4" data-testid="attendance-qr-image">
              <QRCodeSVG value={qr.qr_payload} size={248} level="M" />
            </div>
            <p className="mt-4 text-sm font-semibold text-slate-700">
              Valido fino alle {expiryText}
            </p>
            <p className="mt-2 text-xs text-slate-500">Il codice cambia dopo una timbratura o alla scadenza. Connessione internet necessaria.</p>
          </>
        ) : loading || paired || error ? (
          <p className="mt-7 text-sm text-slate-600" role="status">
            {loading ? 'Caricamento QR…' : 'QR temporaneamente non disponibile.'}
          </p>
        ) : (
          <form onSubmit={enroll} className="mt-7 w-full max-w-sm space-y-3" data-testid="attendance-qr-kiosk-enroll">
            <p className="text-sm text-slate-600">Attiva questo schermo con il codice temporaneo generato da un responsabile.</p>
            <label htmlFor="qr-kiosk-pairing-code" className="sr-only">Codice di attivazione</label>
            <input
              id="qr-kiosk-pairing-code"
              autoComplete="off"
              value={pairingCode}
              onChange={(event) => setPairingCode(event.target.value)}
              className="min-h-12 w-full rounded-xl border border-slate-300 px-4 text-center font-mono text-sm"
              placeholder="Codice di attivazione"
            />
            <button
              type="submit"
              disabled={busy || pairingCode.trim().length < 40}
              className="min-h-12 w-full rounded-xl bg-emerald-600 px-4 font-semibold text-white disabled:opacity-50"
            >
              {busy ? 'Attivazione…' : 'Attiva dispositivo'}
            </button>
          </form>
        )}
        {error && (
          <div className="mt-5 max-w-md rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-900" role="alert" data-testid="attendance-qr-kiosk-error">
            <p>{error}</p>
            <button type="button" onClick={refreshQr} disabled={loading}
              className="mt-3 rounded-lg border border-amber-800 px-4 py-2 disabled:opacity-50">
              Riprova
            </button>
          </div>
        )}
      </section>
    </main>
  );
}
